/**
 * Phase-5 Worker identities management page.
 *
 * This is the admin-facing surface for provisioning and managing worker
 * credentials (OAuth client_credentials) per
 * `docs/implementation/47l_ADMIN_AND_USER_UI_SURFACES.md §4.2`.
 *
 * Vocabulary rules: no NATS concepts appear on screen. Workers are
 * "API credentials for this worker"; operations are add / rotate /
 * pause / revoke.
 */

import { useEffect, useMemo, useState } from 'react';
import {
   AlertTriangle,
   Check,
   Circle,
   Copy,
   KeyRound,
   Pause,
   Play,
   Plus,
   Server,
   Trash2,
} from 'lucide-react';
import { usePageTitle } from '@/hooks/use-page-title';
import { useHeaderAction } from '@/lib/header-action-context';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
   AlertDialog,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import CreateWorkerDialog from '@/components/workers/CreateWorkerDialog';
import {
   usePauseWorker,
   useRevokeWorker,
   useRotateWorkerSecret,
   useWorkers,
   type WorkerIdentity,
   type WorkerSecretReveal,
} from '@/hooks/use-workers';
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from '@/components/ui/select';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { cn } from '@/lib/utils';
import type { WorkerStatus, WorkerType } from '@/hooks/use-workers';

type StatusTab = 'all' | WorkerStatus;

const STATUS_TABS: { key: StatusTab; label: string }[] = [
   { key: 'all', label: 'All' },
   { key: 'active', label: 'Active' },
   { key: 'paused', label: 'Paused' },
   { key: 'revoked', label: 'Revoked' },
];

type PendingAction =
   | { kind: 'pause'; worker: WorkerIdentity }
   | { kind: 'revoke'; worker: WorkerIdentity }
   | { kind: 'rotate'; worker: WorkerIdentity }
   | null;

const statusPalette: Record<WorkerIdentity['status'], { dot: string; bg: string; text: string; label: string }> = {
   active: { dot: 'text-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Active' },
   paused: { dot: 'text-amber-500', bg: 'bg-amber-50', text: 'text-amber-700', label: 'Paused' },
   revoked: { dot: 'text-slate-400', bg: 'bg-slate-100', text: 'text-slate-500', label: 'Revoked' },
};

const workerTypeLabel: Record<WorkerIdentity['worker_type'], string> = {
   platform: 'Platform',
   self_hosted: 'Self-hosted',
   third_party: 'Third-party',
};

function formatDate(iso: string | null | undefined): string {
   if (!iso) return '—';
   try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
   } catch {
      return iso;
   }
}

const createWorkerButtonClass =
   'inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2';

export default function WorkersPage() {
   usePageTitle('Workers');
   const { setHeaderAction } = useHeaderAction();
   const [createOpen, setCreateOpen] = useState(false);
   const [pending, setPending] = useState<PendingAction>(null);
   const [rotatedCredentials, setRotatedCredentials] = useState<WorkerSecretReveal | null>(null);
   const [copiedId, setCopiedId] = useState(false);
   const [copiedSecret, setCopiedSecret] = useState(false);
   const [searchQuery, setSearchQuery] = useState('');
   const [activeTab, setActiveTab] = useState<StatusTab>('all');
   const [typeFilter, setTypeFilter] = useState<'all' | WorkerType>('all');
   const [sortField, setSortField] = useState('name');
   const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

   const { data, isLoading, isError, refetch } = useWorkers();
   const pauseMutation = usePauseWorker();
   const revokeMutation = useRevokeWorker();
   const rotateMutation = useRotateWorkerSecret();

   const workers = data?.workers ?? [];

   const tabCounts = useMemo(() => {
      const counts: Record<StatusTab, number> = { all: 0, active: 0, paused: 0, revoked: 0 };
      for (const w of workers) {
         counts.all++;
         counts[w.status]++;
      }
      return counts;
   }, [workers]);

   const filteredWorkers = useMemo(() => {
      const q = searchQuery.trim().toLowerCase();
      const filtered = workers.filter((worker) => {
         const matchesTab = activeTab === 'all' || worker.status === activeTab;
         const matchesType = typeFilter === 'all' || worker.worker_type === typeFilter;
         const matchesSearch =
            !q ||
            worker.name.toLowerCase().includes(q) ||
            (worker.description ?? '').toLowerCase().includes(q) ||
            workerTypeLabel[worker.worker_type].toLowerCase().includes(q) ||
            String(worker.allowed_capability_ids.length).includes(q);
         return matchesTab && matchesType && matchesSearch;
      });

      return [...filtered].sort((a, b) => {
         let cmp = 0;
         if (sortField === 'status') {
            cmp = a.status.localeCompare(b.status);
         } else if (sortField === 'created') {
            cmp = a.created.localeCompare(b.created);
         } else {
            cmp = a.name.localeCompare(b.name);
         }
         return sortDirection === 'asc' ? cmp : -cmp;
      });
   }, [workers, searchQuery, activeTab, typeFilter, sortField, sortDirection]);

   const handleSort = (field: string) => {
      if (sortField === field) {
         setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
         setSortField(field);
         setSortDirection('asc');
      }
   };

   useEffect(() => {
      setHeaderAction(
         <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className={createWorkerButtonClass}
            data-testid="workers-header-add"
         >
            <Plus size={14} />
            Add Worker
         </button>,
      );
      return () => setHeaderAction(null);
   }, [setHeaderAction]);

   const handlePending = async () => {
      if (!pending) return;
      try {
         if (pending.kind === 'pause') {
            await pauseMutation.mutateAsync(pending.worker.id);
         } else if (pending.kind === 'revoke') {
            await revokeMutation.mutateAsync(pending.worker.id);
         } else if (pending.kind === 'rotate') {
            const res = await rotateMutation.mutateAsync(pending.worker.id);
            setRotatedCredentials(res);
         }
         setPending(null);
         refetch();
      } catch {
         // mutation error is surfaced by the dialog's disabled state
      }
   };

   const copyToClipboard = async (text: string, which: 'id' | 'secret') => {
      try {
         await navigator.clipboard.writeText(text);
         if (which === 'id') {
            setCopiedId(true);
            setTimeout(() => setCopiedId(false), 2000);
         } else {
            setCopiedSecret(true);
            setTimeout(() => setCopiedSecret(false), 2000);
         }
      } catch {
         // ignore
      }
   };

   const isMutating = pauseMutation.isPending || revokeMutation.isPending || rotateMutation.isPending;

   const tabBar = (
      <div className="flex items-center rounded-md border border-[hsl(var(--list-controls-border))] bg-[hsl(var(--list-controls-surface))] p-1">
         {STATUS_TABS.map((tab) => (
            <button
               key={tab.key}
               type="button"
               onClick={() => setActiveTab(tab.key)}
               className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  activeTab === tab.key
                     ? 'bg-[hsl(var(--list-controls-sort-active-bg))] text-[hsl(var(--list-controls-sort-active-fg))] shadow-sm'
                     : 'text-[hsl(var(--list-controls-muted-foreground))] hover:bg-[hsl(var(--list-controls-surface-hover))] hover:text-[hsl(var(--list-controls-foreground))]',
               )}
            >
               {tab.label}
               <span
                  className={cn(
                     'rounded-full px-1.5 py-0.5 text-xs font-medium',
                     activeTab === tab.key
                        ? 'bg-white/20 text-[hsl(var(--list-controls-sort-active-fg))]'
                        : 'bg-[hsl(var(--list-controls-border))] text-[hsl(var(--list-controls-muted-foreground))]',
                  )}
               >
                  {tabCounts[tab.key]}
               </span>
            </button>
         ))}
      </div>
   );

   const typeSelect = (
      <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as 'all' | WorkerType)}>
         <SelectTrigger className={cn('w-44', listControlsGlassTrigger)}>
            <SelectValue placeholder="All types" />
         </SelectTrigger>
         <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="platform">Platform</SelectItem>
            <SelectItem value="self_hosted">Self-hosted</SelectItem>
            <SelectItem value="third_party">Third-party</SelectItem>
         </SelectContent>
      </Select>
   );

   return (
      <div className="flex min-h-0 flex-1 flex-col">
         {isError ? (
            <Card className="p-8">
               <p className="text-center text-sm text-red-600">
                  Unable to load workers. Please refresh the page.
               </p>
            </Card>
         ) : (
            <CapabilityListPanel<WorkerIdentity>
               items={filteredWorkers}
               sortOptions={[
                  { key: 'name', label: 'Name' },
                  { key: 'status', label: 'Status' },
                  { key: 'created', label: 'Created' },
               ]}
               sortField={sortField}
               sortDirection={sortDirection}
               onSort={handleSort}
               searchQuery={searchQuery}
               onSearchChange={setSearchQuery}
               searchPlaceholder="Search workers..."
               allTags={[]}
               selectedTags={new Set<string>()}
               onToggleTag={() => {}}
               onClearAllTags={() => {}}
               onSelectAllTags={() => {}}
               showTagsFilter={false}
               preSearchToolbar={tabBar}
               extraToolbar={typeSelect}
               isLoading={isLoading}
               loadingMessage="Loading workers..."
               emptyStateVariant="glass"
               emptyMessage="No workers yet. Add one to grant process credentials."
               gridClassName="grid grid-cols-1 gap-3"
               renderCard={(worker) => {
                  const status = statusPalette[worker.status] ?? statusPalette.active;
                  const disabled = worker.status === 'revoked';
                  return (
                     <Card
                        key={worker.id}
                        className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between"
                        data-testid={`worker-row-${worker.id}`}
                     >
                        <div className="flex min-w-0 items-start gap-3">
                           <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-emerald-50">
                              <Server className="h-5 w-5 text-emerald-600" />
                           </div>
                           <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                 <h3 className="text-sm font-semibold text-slate-900">{worker.name}</h3>
                                 <span
                                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${status.bg} ${status.text}`}
                                 >
                                    <Circle size={7} className={`fill-current ${status.dot}`} />
                                    {status.label}
                                 </span>
                                 <span className="text-xs text-slate-500">
                                    {workerTypeLabel[worker.worker_type]}
                                 </span>
                              </div>
                              {worker.description ? (
                                 <p className="mt-1 text-xs text-slate-500">{worker.description}</p>
                              ) : null}
                              <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                                 <div>
                                    <dt className="inline text-slate-400">Capabilities: </dt>
                                    <dd className="inline font-medium text-slate-600">
                                       {worker.allowed_capability_ids.length}
                                    </dd>
                                 </div>
                                 <div>
                                    <dt className="inline text-slate-400">Last connected: </dt>
                                    <dd className="inline">{formatDate(worker.last_connected_at)}</dd>
                                 </div>
                                 <div>
                                    <dt className="inline text-slate-400">Secret rotated: </dt>
                                    <dd className="inline">{formatDate(worker.last_secret_rotated_at)}</dd>
                                 </div>
                              </dl>
                           </div>
                        </div>

                        <div className="flex shrink-0 flex-wrap gap-2">
                           <Button
                              variant="outline"
                              size="sm"
                              disabled={disabled || isMutating}
                              onClick={() => setPending({ kind: 'rotate', worker })}
                              data-testid={`worker-rotate-${worker.id}`}
                           >
                              <KeyRound className="mr-1.5 h-4 w-4" />
                              Rotate
                           </Button>
                           <Button
                              variant="outline"
                              size="sm"
                              disabled={disabled || isMutating}
                              onClick={() => setPending({ kind: 'pause', worker })}
                              data-testid={`worker-pause-${worker.id}`}
                           >
                              {worker.status === 'paused' ? (
                                 <>
                                    <Play className="mr-1.5 h-4 w-4" />
                                    Paused
                                 </>
                              ) : (
                                 <>
                                    <Pause className="mr-1.5 h-4 w-4" />
                                    Pause
                                 </>
                              )}
                           </Button>
                           <Button
                              variant="outline"
                              size="sm"
                              disabled={disabled || isMutating}
                              className="text-destructive hover:bg-destructive/10"
                              onClick={() => setPending({ kind: 'revoke', worker })}
                              data-testid={`worker-revoke-${worker.id}`}
                           >
                              <Trash2 className="mr-1.5 h-4 w-4" />
                              Revoke
                           </Button>
                        </div>
                     </Card>
                  );
               }}
            />
         )}

         <CreateWorkerDialog
            isOpen={createOpen}
            onClose={() => setCreateOpen(false)}
            onCreated={() => {
               setCreateOpen(false);
               refetch();
            }}
         />

         <AlertDialog
            open={!!pending}
            onOpenChange={(open) => {
               if (!open && !isMutating) setPending(null);
            }}
         >
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>
                     {pending?.kind === 'rotate'
                        ? `Rotate secret for ${pending.worker.name}?`
                        : pending?.kind === 'pause'
                          ? `${pending.worker.status === 'paused' ? 'Resume' : 'Pause'} ${pending.worker.name}?`
                          : pending?.kind === 'revoke'
                            ? `Revoke ${pending.worker.name}?`
                            : ''}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                     {pending?.kind === 'rotate'
                        ? 'The current secret will be invalidated immediately. You will receive a new secret shown once.'
                        : pending?.kind === 'pause'
                          ? pending.worker.status === 'paused'
                             ? 'The worker will be able to connect again.'
                             : 'The worker will lose access until you resume it.'
                          : pending?.kind === 'revoke'
                            ? 'This action cannot be undone. The worker will be permanently disabled.'
                            : ''}
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel disabled={isMutating}>Cancel</AlertDialogCancel>
                  <Button
                     variant={pending?.kind === 'revoke' ? 'destructive' : 'default'}
                     disabled={isMutating}
                     onClick={() => void handlePending()}
                     data-testid="worker-action-confirm"
                  >
                     {isMutating
                        ? 'Working…'
                        : pending?.kind === 'rotate'
                          ? 'Rotate secret'
                          : pending?.kind === 'pause'
                            ? pending.worker.status === 'paused'
                               ? 'Resume'
                               : 'Pause'
                            : pending?.kind === 'revoke'
                              ? 'Revoke'
                              : 'Confirm'}
                  </Button>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         {rotatedCredentials ? (
            <AlertDialog open={true} onOpenChange={(open) => !open && setRotatedCredentials(null)}>
               <AlertDialogContent>
                  <AlertDialogHeader>
                     <AlertDialogTitle>New secret issued</AlertDialogTitle>
                     <AlertDialogDescription>
                        Save this; we won&apos;t show it again. The previous secret has been invalidated.
                     </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="space-y-3 py-2">
                     <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                        Copy the secret now — it is the only time it will be shown.
                     </div>
                     <div className="space-y-1.5">
                        <Label>Worker ID</Label>
                        <div className="flex gap-2">
                           <Input
                              readOnly
                              value={rotatedCredentials.client_id}
                              className="font-mono text-sm"
                              data-testid="rotated-id"
                           />
                           <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => copyToClipboard(rotatedCredentials.client_id, 'id')}
                           >
                              {copiedId ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                           </Button>
                        </div>
                     </div>
                     <div className="space-y-1.5">
                        <Label>Worker Secret</Label>
                        <div className="flex gap-2">
                           <Input
                              readOnly
                              type="password"
                              value={rotatedCredentials.client_secret}
                              className="font-mono text-sm"
                              data-testid="rotated-secret"
                           />
                           <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => copyToClipboard(rotatedCredentials.client_secret, 'secret')}
                           >
                              {copiedSecret ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                           </Button>
                        </div>
                     </div>
                  </div>
                  <AlertDialogFooter>
                     <Button type="button" onClick={() => setRotatedCredentials(null)}>
                        Done
                     </Button>
                  </AlertDialogFooter>
               </AlertDialogContent>
            </AlertDialog>
         ) : null}
      </div>
   );
}
